import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2, ArrowRight, ArrowLeft, PartyPopper } from "lucide-react";
import api, { money, apiErr, fmtDate } from "@/lib/api";
import { PageHero, Field } from "@/components/site/ui";
import { useAuth } from "@/context/AuthContext";

const inputCls = "w-full bg-black/30 border border-white/10 rounded-md px-3 py-2.5 text-sm text-foreground focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors";

export default function Book() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [experiences, setExperiences] = useState([]);
  const [step, setStep] = useState(1);
  const [quote, setQuote] = useState(null);
  const [avail, setAvail] = useState(null);
  const [checking, setChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const [f, setF] = useState({
    experience_id: params.get("experience") || "",
    arrival_date: "", departure_date: "", arrival_time: "16:00", departure_time: "11:00",
    adults: 2, children: 0, guest_names: "", contact_email: user?.email || "",
    contact_phone: "", billing_address: "", emergency_contact: "", vehicle: "",
    special_requests: "", add_on_ids: [], invitation_code: params.get("invite") || "",
    accepted_policies: false, signature: "",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  useEffect(() => { api.get("/experiences").then((r) => setExperiences(r.data)).catch(() => {}); }, []);
  const exp = useMemo(() => experiences.find((e) => e.id === f.experience_id), [experiences, f.experience_id]);
  const [addons, setAddons] = useState([]);
  useEffect(() => { if (f.experience_id) api.get(`/experiences/${f.experience_id}`).then((r) => setAddons(r.data.addons || [])).catch(() => setAddons([])); }, [f.experience_id]);

  const canQuote = f.experience_id && f.arrival_date && f.departure_date && f.departure_date >= f.arrival_date;
  useEffect(() => {
    if (!canQuote) { setQuote(null); return; }
    const body = { experience_id: f.experience_id, arrival_date: f.arrival_date, departure_date: f.departure_date, adults: +f.adults, children: +f.children, add_on_ids: f.add_on_ids, membership_level: user?.membership_level || "Guest" };
    api.post("/pricing/quote", body).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [f.experience_id, f.arrival_date, f.departure_date, f.adults, f.children, f.add_on_ids, canQuote, user]);

  const checkAvail = async () => {
    if (!canQuote) { toast.error("Choose an experience and valid dates first."); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/availability/check", { experience_id: f.experience_id, arrival_date: f.arrival_date, departure_date: f.departure_date });
      setAvail(data);
      if (data.available) { toast.success("Those dates are available."); setStep(2); }
      else toast.error("Those dates are not available. Please adjust.");
    } catch (e) { toast.error(apiErr(e)); } finally { setChecking(false); }
  };

  const toggleAddon = (id) => set("add_on_ids", f.add_on_ids.includes(id) ? f.add_on_ids.filter((x) => x !== id) : [...f.add_on_ids, id]);

  const submit = async () => {
    if (!f.accepted_policies) { toast.error("Please accept the policies to continue."); return; }
    if (!f.signature.trim()) { toast.error("Please sign to acknowledge the house rules."); return; }
    setSubmitting(true);
    try {
      const body = { ...f, adults: +f.adults, children: +f.children, guest_names: f.guest_names.split(",").map((s) => s.trim()).filter(Boolean), source: f.invitation_code ? "invitation_code" : "public_request" };
      const { data } = await api.post("/reservations", body);
      setDone(data);
      window.scrollTo(0, 0);
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d && typeof d === "object" && d.conflicts) toast.error("Those dates are no longer available.");
      else toast.error(apiErr(e));
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div>
        <PageHero eyebrow="Reservation Requested" title="Welcome to The D" compact />
        <div className="max-w-xl mx-auto px-6 py-20 text-center">
          <div className="glass rounded-lg p-10">
            <PartyPopper className="text-gold mx-auto mb-5" size={36} />
            <h2 className="font-serif text-3xl">Your request is in.</h2>
            <p className="text-muted-foreground mt-3 text-sm">{done.message}</p>
            <div className="my-8 py-6 border-y border-gold/20">
              <p className="text-xs tracking-label uppercase text-muted-foreground">Confirmation Number</p>
              <p className="font-serif text-4xl text-gold mt-2" data-testid="confirmation-number">{done.confirmation_number}</p>
            </div>
            <p className="text-sm text-muted-foreground">Save this number. You can view your reservation any time.</p>
            <div className="flex flex-col sm:flex-row gap-3 mt-8">
              <button onClick={() => nav(`/manage?c=${done.confirmation_number}`)} className="btn-ghost-gold flex-1 py-3 text-sm">Manage Reservation</button>
              <button onClick={() => nav(user ? "/portal" : "/register")} className="btn-gold flex-1 py-3 text-sm">{user ? "My Account" : "Create Account"}</button>
            </div>
          </div>
          <p className="mt-8 font-serif italic text-gold">Never forget the difference you make.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero eyebrow="Reservations" title="Book Your Stay" subtitle="Every reservation is reviewed personally by your host." compact />
      <div className="max-w-5xl mx-auto px-6 py-16 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          {/* Steps */}
          <div className="flex items-center gap-2 mb-8 text-xs">
            {["Dates & Party", "Details & Add-ons", "Review & Sign"].map((s, i) => (
              <div key={s} className={`flex items-center gap-2 ${step === i + 1 ? "text-gold" : "text-muted-foreground"}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center border text-[11px] ${step > i + 1 ? "bg-gold text-ink border-gold" : step === i + 1 ? "border-gold" : "border-white/20"}`}>{step > i + 1 ? <Check size={12} /> : i + 1}</span>
                <span className="hidden sm:inline tracking-label uppercase">{s}</span>
                {i < 2 && <span className="w-6 gold-line mx-1" />}
              </div>
            ))}
          </div>

          {step === 1 && (
            <div className="space-y-5">
              <Field label="Experience" required>
                <select data-testid="book-experience" className={inputCls} value={f.experience_id} onChange={(e) => set("experience_id", e.target.value)}>
                  <option value="">Select an experience…</option>
                  {experiences.map((e) => <option key={e.id} value={e.id}>{e.title}</option>)}
                </select>
              </Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Arrival date" required><input type="date" data-testid="book-arrival" className={inputCls} value={f.arrival_date} onChange={(e) => set("arrival_date", e.target.value)} /></Field>
                <Field label="Departure date" required><input type="date" data-testid="book-departure" className={inputCls} value={f.departure_date} min={f.arrival_date} onChange={(e) => set("departure_date", e.target.value)} /></Field>
                <Field label="Arrival time"><input type="time" className={inputCls} value={f.arrival_time} onChange={(e) => set("arrival_time", e.target.value)} /></Field>
                <Field label="Departure time"><input type="time" className={inputCls} value={f.departure_time} onChange={(e) => set("departure_time", e.target.value)} /></Field>
                <Field label="Adults" required><input type="number" min="1" data-testid="book-adults" className={inputCls} value={f.adults} onChange={(e) => set("adults", e.target.value)} /></Field>
                <Field label="Children"><input type="number" min="0" data-testid="book-children" className={inputCls} value={f.children} onChange={(e) => set("children", e.target.value)} /></Field>
              </div>
              <Field label="Invitation code" hint="Optional — enter if you received a private invitation."><input data-testid="book-invite" className={inputCls} value={f.invitation_code} onChange={(e) => set("invitation_code", e.target.value.toUpperCase())} /></Field>
              {avail && !avail.available && (
                <div className="text-sm text-red-300 glass rounded-md p-4">These dates conflict with an existing reservation or a blocked period. Please choose different dates.</div>
              )}
              <button onClick={checkAvail} disabled={checking || !canQuote} data-testid="book-check-avail" className="btn-gold px-7 py-3 text-sm flex items-center gap-2 disabled:opacity-50">
                {checking ? <Loader2 className="animate-spin" size={16} /> : null} Check Availability <ArrowRight size={16} />
              </button>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Contact email" required><input type="email" data-testid="book-email" className={inputCls} value={f.contact_email} onChange={(e) => set("contact_email", e.target.value)} /></Field>
                <Field label="Mobile phone"><input data-testid="book-phone" className={inputCls} value={f.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} /></Field>
              </div>
              <Field label="Guest names" hint="Separate names with commas."><input data-testid="book-guests" className={inputCls} value={f.guest_names} onChange={(e) => set("guest_names", e.target.value)} placeholder="Jane Doe, John Doe" /></Field>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Billing address"><input className={inputCls} value={f.billing_address} onChange={(e) => set("billing_address", e.target.value)} /></Field>
                <Field label="Emergency contact"><input className={inputCls} value={f.emergency_contact} onChange={(e) => set("emergency_contact", e.target.value)} /></Field>
              </div>
              <Field label="Vehicle & license plate"><input className={inputCls} value={f.vehicle} onChange={(e) => set("vehicle", e.target.value)} /></Field>
              <Field label="Special requests"><textarea rows={3} data-testid="book-requests" className={inputCls} value={f.special_requests} onChange={(e) => set("special_requests", e.target.value)} /></Field>
              {addons.length > 0 && (
                <div>
                  <span className="text-xs tracking-label uppercase text-muted-foreground">Add-ons</span>
                  <div className="grid sm:grid-cols-2 gap-2 mt-2">
                    {addons.map((a) => (
                      <button key={a.id} onClick={() => toggleAddon(a.id)} data-testid={`book-addon-${a.id}`}
                        className={`text-left glass rounded-md px-4 py-3 border transition-colors ${f.add_on_ids.includes(a.id) ? "border-gold" : "border-white/10"}`}>
                        <div className="flex justify-between items-center">
                          <span className="text-sm">{a.name}</span>
                          <span className="text-gold text-xs">{a.charge_type === "included" || a.charge_type === "complimentary" ? "Included" : money(a.price)}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3">
                <button onClick={() => setStep(1)} className="btn-ghost-gold px-6 py-3 text-sm flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
                <button onClick={() => setStep(3)} data-testid="book-to-review" disabled={!f.contact_email} className="btn-gold px-7 py-3 text-sm flex items-center gap-2 disabled:opacity-50">Review <ArrowRight size={16} /></button>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <div className="glass rounded-lg p-6 text-sm space-y-2">
                <div className="flex justify-between"><span className="text-muted-foreground">Experience</span><span>{exp?.title}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Dates</span><span>{fmtDate(f.arrival_date)} → {fmtDate(f.departure_date)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Party</span><span>{f.adults} adults · {f.children} children</span></div>
              </div>
              <div className="glass rounded-lg p-6">
                <h4 className="font-serif text-xl mb-3">House Rules & Policies</h4>
                <p className="text-xs text-muted-foreground mb-4">Check-in {exp ? "" : ""} is subject to host approval. Quiet hours 10PM–8AM. No lifeguard on duty — guests swim at their own risk. Full policies are shown at digital check-in.</p>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" data-testid="book-accept-policies" checked={f.accepted_policies} onChange={(e) => set("accepted_policies", e.target.checked)} className="mt-1 accent-gold" />
                  <span className="text-sm">I have read and accept The D's policies, house rules, and pool safety acknowledgment.</span>
                </label>
                <div className="mt-4">
                  <Field label="Digital signature (type your full name)" required>
                    <input data-testid="book-signature" className={inputCls} value={f.signature} onChange={(e) => set("signature", e.target.value)} placeholder="Your full legal name" />
                  </Field>
                </div>
              </div>
              <div className="flex gap-3">
                <button onClick={() => setStep(2)} className="btn-ghost-gold px-6 py-3 text-sm flex items-center gap-2"><ArrowLeft size={16} /> Back</button>
                <button onClick={submit} disabled={submitting} data-testid="book-submit" className="btn-gold px-8 py-3 text-sm flex items-center gap-2 disabled:opacity-50">
                  {submitting ? <Loader2 className="animate-spin" size={16} /> : null} Submit Reservation Request
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Live summary */}
        <div className="lg:col-span-1">
          <div className="glass rounded-lg p-6 sticky top-28">
            <h4 className="text-xs tracking-label uppercase text-gold mb-4">Your Reservation</h4>
            {exp ? (
              <>
                <p className="font-serif text-2xl">{exp.title}</p>
                <p className="text-xs text-muted-foreground mt-1">{exp.category}</p>
              </>
            ) : <p className="text-sm text-muted-foreground">Select an experience to begin.</p>}
            <div className="gold-line my-5" />
            {quote ? (
              <div className="space-y-2 text-sm">
                {quote.items.map((it) => (
                  <div key={it.id} className="flex justify-between">
                    <span className={`text-muted-foreground ${it.kind === "discount" ? "text-pool" : ""}`}>{it.label}</span>
                    <span>{money(it.amount)}</span>
                  </div>
                ))}
                <div className="gold-line my-3" />
                <div className="flex justify-between font-medium"><span>Total</span><span className="text-gold">{money(quote.total)}</span></div>
                <div className="flex justify-between text-xs text-muted-foreground"><span>Deposit to reserve</span><span>{money(quote.deposit)}</span></div>
              </div>
            ) : <p className="text-sm text-muted-foreground">Choose dates to see pricing.</p>}
            <p className="text-[11px] text-muted-foreground mt-5">The D does not process card payments online. Your host will arrange payment directly.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
